import type { AppEvent } from "./types";
import { normalizeCourseKey } from "../../shared/utils/normalizeCourse";

type IcsEventRaw = Record<string, string[]>;

function djb2(str: string) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(16);
}

function unfoldLines(ics: string) {
  // RFC5545 line folding: lines starting with space/tab are continuation
  const lines = ics.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  for (const line of lines) {
    if (!line) continue;
    if (/^[ \t]/.test(line) && out.length) out[out.length - 1] += line.slice(1);
    else out.push(line);
  }
  return out;
}

function parseIcsRaw(ics: string): IcsEventRaw[] {
  const lines = unfoldLines(ics);
  const events: IcsEventRaw[] = [];
  let cur: IcsEventRaw | null = null;

  for (const ln of lines) {
    if (ln === "BEGIN:VEVENT") {
      cur = {};
      continue;
    }
    if (ln === "END:VEVENT") {
      if (cur) events.push(cur);
      cur = null;
      continue;
    }
    if (!cur) continue;

    const idx = ln.indexOf(":");
    if (idx <= 0) continue;

    const left = ln.slice(0, idx);
    const value = ln.slice(idx + 1);

    // Left could be like "DTSTART;TZID=Asia/Almaty"
    const key = left.split(";")[0].toUpperCase();

    if (!cur[key]) cur[key] = [];
    cur[key].push(value);
  }

  return events;
}

function parseIcsDate(v?: string): string | null {
  if (!v) return null;

  const s = v.trim();

  // date only
  if (/^\d{8}$/.test(s)) {
    const y = Number(s.slice(0, 4));
    const m = Number(s.slice(4, 6)) - 1;
    const d = Number(s.slice(6, 8));
    return new Date(y, m, d, 0, 0, 0).toISOString();
  }

  const z = s.endsWith("Z");
  const core = z ? s.slice(0, -1) : s;

  if (/^\d{8}T\d{6}$/.test(core)) {
    const y = Number(core.slice(0, 4));
    const m = Number(core.slice(4, 6)) - 1;
    const d = Number(core.slice(6, 8));
    const hh = Number(core.slice(9, 11));
    const mm = Number(core.slice(11, 13));
    const ss = Number(core.slice(13, 15));

    if (z) return new Date(Date.UTC(y, m, d, hh, mm, ss)).toISOString();
    return new Date(y, m, d, hh, mm, ss).toISOString();
  }

  const dt = new Date(s);
  if (!Number.isFinite(dt.getTime())) return null;
  return dt.toISOString();
}

function pickFirst(raw: IcsEventRaw, key: string) {
  return raw[key]?.[0];
}

function cleanText(s?: string) {
  if (!s) return "";
  return s
    .replace(/\\n/g, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/&amp;/g, "&")
    .trim();
}


function extractUrlFromText(text: string): string {
  const m = text.match(/https?:\/\/[^\s)>"']+/i);
  return m ? m[0] : "";
}

function guessCourseAndTitle(summary: string): { courseName?: string; title: string } {
  const s = summary.trim();
  const parts = s.split(" - ");
  if (parts.length >= 2) {
    const courseName = parts[0].trim();
    const title = parts.slice(1).join(" - ").trim();
    if (courseName.length >= 3 && title.length >= 2) return { courseName, title };
  }
  return { title: s };
}


function pickBetterTitle(summary: string, description: string): string {
  const s = summary.trim();
  const d = description.trim();

  // If summary is just a number deadline like "6. is due" -> normalize to "Assignment 6"
  const numMatch = s.match(/^\s*(\d+)\.?\s+is due\s*$/i);
  if (numMatch) return `Assignment ${numMatch[1]}`;

  const looksGeneric = /opens\s*$/i.test(s) || /is due\s*$/i.test(s);
  const tooShort = s.length <= 20;
  if (!looksGeneric || !tooShort || !d) return s;

  const lines = d.split(/\n+/).map((x) => x.trim()).filter(Boolean);
  const candidates = lines.filter((line) => !/^https?:\/\//i.test(line));
  if (candidates.length === 0) return s;

  // Prefer lines with keywords; otherwise take the longest meaningful line
  const keyword = candidates.find((line) => /quiz|assignment|project|exam|midterm|final/i.test(line));
  if (keyword) return keyword;

  const longest = candidates.reduce((a, b) => (b.length > a.length ? b : a), candidates[0]);
  if (/^\[.+\.(png|jpe?g|gif|webp|bmp|pdf|docx?)\]$/i.test(longest)) return s;
  return longest || s;
}

function guessKind(summary: string, url?: string, desc?: string): "lesson" | "deadline" | "assignment" {
  const s = summary.toLowerCase();
  const u = (url || "").toLowerCase();
  const d = (desc || "").toLowerCase();

  const assignmentHints = ["assignment", "homework", "submission", "upload", "quiz", "test"];
  const assignmentTypos = ["assigment", "assignement", "assigment"];
  const assignmentRu = ["задание", "асайнмент", "ассайнмент", "ассигмент"];
  const deadlineHints = ["deadline", "due", "until", "сдать", "дедлайн", "крайний срок"];

  const moodleAssign = u.includes("/mod/assign") || d.includes("/mod/assign");
  const moodleQuiz = u.includes("/mod/quiz") || d.includes("/mod/quiz");

  if (moodleAssign || moodleQuiz) return "assignment";
  if (assignmentHints.some((h) => s.includes(h) || d.includes(h))) return "assignment";
  if (assignmentTypos.some((h) => s.includes(h) || d.includes(h))) return "assignment";
  if (assignmentRu.some((h) => s.includes(h) || d.includes(h))) return "assignment";
  if (deadlineHints.some((h) => s.includes(h) || d.includes(h))) return "deadline";

  return "lesson";
}

function computeUid(rawUid: string | undefined, startIso: string | null, summary: string, location: string) {
  if (rawUid && rawUid.trim()) return `ics:${rawUid.trim()}`;
  const base = `${startIso || ""}|${summary}|${location}`;
  return `ics:h${djb2(base)}`;
}

/**
 * Parse ICS text into AppEvent[]
 */
export function parseIcs(icsText: string): AppEvent[] {
  const raws = parseIcsRaw(icsText);
  const out: AppEvent[] = [];

  for (const r of raws) {
    const summary = cleanText(pickFirst(r, "SUMMARY")) || "Event";
    const location = cleanText(pickFirst(r, "LOCATION")) || "";
    const description = cleanText(pickFirst(r, "DESCRIPTION")) || "";
    const descUrl = extractUrlFromText(description);
    const url = cleanText(pickFirst(r, "URL")) || "";
    const categories = cleanText(pickFirst(r, "CATEGORIES")) || "";

    const dtStart = parseIcsDate(pickFirst(r, "DTSTART"));
    const dtEnd = parseIcsDate(pickFirst(r, "DTEND"));

    if (!dtStart) continue;

    // Moodle sometimes puts "Course Name | Teacher Name" into CATEGORIES.
    // Example:
    //   CATEGORIES:Computer Organisation and Architecture | Gulnur Shuteyeva
    let catCourse: string | undefined;
    let catTeacher: string | undefined;
    if (categories.includes("|")) {
      const parts = categories.split("|").map((x) => x.trim()).filter(Boolean);
      if (parts.length >= 1) catCourse = parts[0];
      if (parts.length >= 2) catTeacher = parts.slice(1).join(" | ");
    }

    const betterTitle = pickBetterTitle(summary, description);
    const guessed = guessCourseAndTitle(betterTitle);
    const courseName = catCourse || guessed.courseName;
    // If Moodle uses "Attendance (Group ...)", show the real subject as the title when possible.
    const title = /^attendance\b/i.test(summary) && courseName ? courseName : guessed.title;
    const courseKey = normalizeCourseKey(courseName || title);

    const finalUrl = url || descUrl;
    const kind = guessKind(summary, finalUrl, description);

    let due: string | undefined = undefined;
    if (kind === "deadline" || kind === "assignment") {
      const st = new Date(dtStart);
      const originalWasDateOnly = /^\d{8}$/.test((pickFirst(r, "DTSTART") || "").trim());
      if (originalWasDateOnly) {
        due = new Date(st.getFullYear(), st.getMonth(), st.getDate(), 23, 59, 0).toISOString();
      } else {
        due = dtEnd || dtStart;
      }
    }

    const uid = computeUid(pickFirst(r, "UID"), dtStart, summary, location);

    out.push({
      uid,
      kind,
      title,
      start: dtStart,
      end: kind === "lesson" ? dtEnd || undefined : undefined,
      due,
      courseName,
      courseKey: courseKey || undefined,
      room: location || undefined,
      teacher: catTeacher || undefined,
      moodleUrl: finalUrl || undefined,
      description: description || undefined,
      source: { provider: "ics", rawId: pickFirst(r, "UID") || undefined }
    });
  }

  out.sort((a, b) => new Date(a.due || a.start).getTime() - new Date(b.due || b.start).getTime());
  return out;
}
